import { getAggregateError, } from "@bracket/catch";
import { getTokenDefinition, } from "@bracket/chainography";
import { caller, contract, multiCaller, } from "@bracket/chainography/contracts";
import type { WEIString, } from "@bracket/types";
import { numberify, } from "@bracket/typescript-utils";
import * as G from "@globals";
import type { IBracketStore, IdentifiableVaultWithState, LoadedVault, VaultExposure, } from "@types";
import { assetToUsd, usdToAsset, } from "@utils";
import { getLoadedVault, } from "@vault/resolvers";
import { wagmiConfig as config, } from "@wagmiconfig";
import { initialExposure, } from "./initialExposure";




/**
 * Calculates the exposure of the currently connected wallet to a specific vault.
 *
 * This function retrieves the vault details, checks its status, and calculates
 * the user's investment value (in both the vault's denomination and USD),
 * the number of shares they hold, and whether they are a participant.
 * It handles different vault versions by applying version-specific calculations
 * if necessary using `extendedFunctions`. If the vault is not active, it returns
 * initial zeroed exposure data.
 *
 * @param {IdentifiableVaultWithState} identifier - An object identifying the vault (e.g., by address or slug) and potentially its state.
 * @returns {Promise<VaultExposure>} A promise that resolves with the calculated exposure data for the user in the specified vault.
 */
export const getVaultExposure = async ( store: IBracketStore, identifier: IdentifiableVaultWithState, ): Promise<VaultExposure> => {

	const vault = await getLoadedVault(store, identifier);

	if(vault.status !== "ACTIVE") {
		return initialExposure;
	}

	const result: VaultExposure = await getBaseData(vault);
	// TODO: Define or import extendedFunctions if version-specific logic is needed.
	// Currently, this loop might not do anything if extendedFunctions is empty or undefined.
	// Assuming extendedFunctions is an array of functions indexed by version (minus 1).
	// for(let i = 0; i < ver; i++) {
	// 	if (extendedFunctions[i]) {
	// 		result = await extendedFunctions[i](result);
	// 	}
	// }
	return result;
}


const getBaseData = async (
	vault: LoadedVault,
): Promise<VaultExposure> => {

	const {
		symbol,
		denomination: {
			decimals,
		},
	} = vault;

	const { address:wallet, } = G.wallet.getBracketWallet();
	const denominator = getTokenDefinition(vault.denomination);

	if(!wallet) {
		throw new Error("getVaultExposure called whilst no address is set");
	}

	try {

		const contractName = vault.version === null ? "BracketVault" : "BracketVaultV2";

		const [ [ nav, raw_shares, vault_balance, ], errors ] = await multiCaller<[ bigint, bigint, bigint, ]>(config, [
			contract(contractName, "assetsPerShare", vault.address),
			contract(contractName, "sharesOf", wallet, vault.address),
			contract(contractName, "balanceOf", wallet, vault.address),
		]);

		if(errors) {
			throw getAggregateError(errors, `getVaultExposure::getBaseData failed for vault ${vault.symbol}`);
		}

		const invested_brkt = raw_shares * nav;
		const invested_usd = await assetToUsd(invested_brkt.toString(), denominator);
		const current_value = numberify(raw_shares, decimals) * numberify(nav, decimals);
		const current_value_usd = await assetToUsd(current_value.toString(), denominator);
		const est_annual_earnings = current_value;
		const est_annual_earnings_usd = await assetToUsd(est_annual_earnings.toString(), denominator);
		const shares = numberify(raw_shares, decimals);
		const shares_pending_withdrawal = symbol === "BBOR1" ? 0 : vault.transactions.reduce((acc, t) => {
			return t.trxn_type === "WITHDRAW" && !t.finalized && t.block > vault.schedulable_block ? acc + numberify(t.shares, decimals) : acc;
		}, 0);

		const active_shares = shares; //numberify(raw_shares, decimals) - shares_pending_withdrawal;

		let exposure: VaultExposure = {
			participant: !!vault.transactions.length,
			invested_brkt,
			invested_usd,
			shares,
			active_shares,
			shares_pending_withdrawal,
			vault_balance,
			current_value: {
				usd: current_value_usd,
				brkt: Number(current_value).toString(),
			},
			cost_basis: {
				usd: 0,
				brkt: "0",
			},
			est_annual_earnings: {
				usd: est_annual_earnings_usd,
				brkt: est_annual_earnings.toString(),
			},
			unrealized_gain: {
				usd: 0,
				brkt: "0",
				perc: 0,
			},
			realized_gain: {
				usd: 0,
				brkt: "0",
			},
			orig_cost_basis: {
				usd: 0,
				brkt: "0",
			},
			pnl: {
				usd: 0,
				brkt: "0",
				perc: 0,
			},
			apy: {
				usd: 0,
				brkt: "0",
				perc: 0,
			}
		};

		const { version, } = vault;
		const iterations = version === null ? 1 : Number(version);
		for(let i = 0; i < iterations; i++) {
			exposure = await extendedFunctions[i](exposure, vault);
		}

		return exposure;

	}
	catch(error) {

		throw getAggregateError(error, `getVaultExposure::getBaseData failed for vault ${vault.symbol}`);
	}

}


const getVaultExposureV2 = async ( exposure: VaultExposure, vault: LoadedVault,): Promise<VaultExposure> => {
	return exposure;
}


const getVaultExposureV1 = async (exposure: VaultExposure, vault: LoadedVault,): Promise<VaultExposure> => {

	try {
		const { address:wallet, } = G.wallet.getBracketWallet();
		if(!wallet) {
			throw new Error("getVaultExposure called whilst no address is set");
		}

		const [ nav, navError ] = await caller<bigint>(config, contract(vault.version === null ? "BracketVault" : "BracketVaultV2", "assetsPerShare", vault.address));

		const { denomination: { decimals, }, } = vault;

		const closed = vault.symbol === "BBOR1";

		const conv_rate = await assetToUsd((10**decimals).toString(), vault.denomination);

		exposure.shares = closed ? 0 : vault.transactions.reduce((acc, t) => {
			if(closed) return 0;
			const { trxn_type, shares, finalized, } = t;
			if(trxn_type === "DEPOSIT") {
				return acc + (Number(shares) / (10**decimals));
			}
			if(trxn_type === "WITHDRAW" && finalized) {
				return acc - (Number(shares) / (10**decimals));
			}
			return acc;
		}, 0);

		exposure.current_value = vault.transactions.reduce((acc, t) => {
			if(closed) return { usd: 0, brkt: "0", };
			const { trxn_type, shares, finalized, } = t;
			
			if(trxn_type === "DEPOSIT") {
				const brkt = bs(n(shares, decimals) * n(nav, decimals), decimals);
				const usd = n(brkt, decimals) * conv_rate;
				return {
					brkt: (Number(acc.brkt) + Number(brkt)).toString(),
					usd: acc.usd + usd,
				};
			}

			if(trxn_type === "WITHDRAW" && finalized) {

				const brkt = bs(n(shares, decimals) * n(nav, decimals), decimals);
				const usd = n(brkt, decimals) * conv_rate;

				return {
					brkt: (Number(acc.brkt) - Number(brkt)).toString(),
					usd: acc.usd - usd,
				};

			}

			return acc;

		}, { usd: 0, brkt: "0", });

		exposure.current_value.brkt = await usdToAsset(exposure.current_value.usd, vault.denomination);

		return exposure;
	}
	catch( err ) {
		throw getAggregateError(err, `getVaultExposure::getVaultExposureV1 failed for vault ${vault.symbol}`);
	}

}


const n = (value: WEIString|bigint, decimals: number): number => {
	return Number(value) / (10**decimals);
}


const bs = (value: number, decimals: number): WEIString => {
	return Math.floor(value * (10**decimals)).toString();
}


const extendedFunctions = [
	getVaultExposureV1,
	getVaultExposureV2,
]
